/**
 * The Widget Design Studio's target **vocabulary** — the ids, the two axes and
 * the shape of one target definition.
 *
 * Its own module by the `*.types.ts` convention, and hoisted for the ~300-line
 * ceiling (`.claude/rules/architecture.md`). A leaf: it imports nothing from
 * `studio-targets.ts`, which re-exports every name here, so no consumer moves.
 *
 * The per-field reasoning came WITH the fields, deliberately. Each of these
 * five is a decision the table below it can only encode, never explain — which
 * draft a target edits, which canvas surface it asserts, why exactly one target
 * seeds a sample conversation, and the two different questions the mode
 * switch asks of a target. Those notes are what make the table readable, and
 * they are also what took the module past the ceiling.
 */

import { isDesignPart } from '@syncanix/shared';

import type { DesignPart } from '@syncanix/shared';
import type { LucideIcon } from 'lucide-react';

/**
 * A target id.
 *
 * Every {@link DesignPart} is also a target id — that is what makes a preview
 * click resolve to an editor target with no lookup table between them — plus the
 * two cross-surface targets that have no clickable part of their own.
 */
export type StudioTargetId = DesignPart | 'brand' | 'language' | 'components';

/** Which draft a target edits — see the ownership rule in the file header. */
export type StudioTargetScope = 'design' | 'surface';

/** The canvas surface a target authors — what the preview shows while you edit it. */
export type StudioPreviewView = 'conversation' | 'launcher';

export interface StudioTargetDef {
  readonly id: StudioTargetId;
  /**
   * The icon shown in the pane header and the settings map.
   *
   * The COMPONENT, not a name to look up. A name would need a second table
   * mapping strings to components, and a typo in it fails at runtime as a
   * missing glyph rather than at compile time.
   */
  readonly icon: LucideIcon;
  /**
   * i18n key for the pane header's mono eyebrow — the FAMILY the target belongs
   * to ("THE PANEL", "THE CONVERSATION"). It is what replaces the object
   * switcher: instead of a control that must be operated, the pane simply states
   * where you are.
   */
  readonly eyebrowKey: string;
  /** i18n key for the target's title, in the pane header and the settings map. */
  readonly titleKey: string;
  /**
   * i18n key for the one-sentence plain-language blurb under the pane header.
   *
   * Not decoration and not an ⓘ hint: this is act-information for the least
   * expert user this surface has (`ui-design.md` §14) — a customer-success
   * person who needs to know what "the panel frame" IS before touching a control
   * in it. It stays visible.
   */
  readonly blurbKey: string;
  /** Which draft the target edits (`surface` = the shared live-widget settings). */
  readonly scope: StudioTargetScope;
  /** The canvas view this target asserts — selecting it shows that surface. */
  readonly view: StudioPreviewView;
  /**
   * Whether entering this target seeds the sample conversation into the canvas.
   *
   * TRUE only for `messages`, whose controls (bubble colours, roundness, fonts,
   * the loading animation) only land on a message that is on screen. Every other
   * target shows the UNSEEDED panel — including `welcome`, which owns the empty
   * state that by definition only shows before the first message.
   */
  readonly seedSample: boolean;
  /**
   * Hide this target from Simple mode — it appears in the pane header's family
   * and in the settings map only once Advanced is on.
   *
   * Exactly one target sets it today, and the reasoning is the whole point of
   * the Simple/Advanced preference: per-primitive component styling is a real, shipped
   * capability that a customer-success person will never need, and it is the
   * single biggest control surface in the studio. Hiding it is what "keep the
   * advanced capabilities but hide them" means here — it is NOT removed, it is
   * one switch away, and every override it holds keeps applying to the live
   * widget whichever mode the operator is in.
   */
  readonly advancedOnly?: boolean;
}

/**
 * The route the studio lives on, and the query param that selects a pane on it.
 *
 * Here rather than in `studio-targets.ts` because the settings-search registry
 * reads both, and that file holds lucide icon COMPONENTS — a runtime import the
 * search index has no business pulling in. Everything in this module is either a
 * type or a plain value, so `lib/` can name a studio pane without the studio's
 * rendering weight coming with it. (`setting-anchors.types.ts` is the in-repo
 * precedent for a `.types.ts` carrying its vocabulary's constants.)
 *
 * `?target=` composes with the `?design=<id>` deep link the studio already
 * reads; neither replaces the other.
 */
export const WIDGET_PATH = '/widget';

/** The query param carrying the pane, beside every other hub's `?tab=`. */
export const STUDIO_TARGET_PARAM = 'target';

/**
 * Is this a target id?
 *
 * Built on the shared `isDesignPart` rather than on a second copy of the seven
 * part ids, so the only ids written twice are the three that are not parts — and
 * those are already written in {@link StudioTargetId} one screen up, where a
 * reader can see them disagree.
 */
export function isStudioTargetId(value: string): value is StudioTargetId {
  return isDesignPart(value) || value === 'brand' || value === 'language' || value === 'components';
}

/**
 * The pane a URL asks for, or `undefined` when it asks for none.
 *
 * `undefined` rather than the default target, deliberately: an ABSENT param and
 * an UNKNOWN one are different states, and only the second is worth rewriting
 * out of the URL. The caller resolves both — see `StudioPage`.
 */
export function readStudioTarget(search: URLSearchParams): StudioTargetId | undefined {
  const raw = search.get(STUDIO_TARGET_PARAM) ?? '';
  return isStudioTargetId(raw) ? raw : undefined;
}
